<template>
  <div ref="prompt" class="fixed inset-0 z-50 w-full h-screen bg-black/40 dark:bg-gray-700/20 flex justify-center items-center">
    <div :class="promptClass">
      <!-- CLOSE BUTTON IF SET -->
      <div v-if="showCloseBtn" class="w-full flex justify-end pt-2">
        <button @click="closePrompt">
          <font-awesome-icon icon="fa-solid fa-xmark" size="xl" />
        </button>
      </div>
      <!-- PROMPT HEADER TEXT -->
      <h1
        v-if="hasAction && promptHeaderText !== 'none'"
        class="font-extrabold text-2xl mt-4 text-primary dark:text-primary-200 tracking-wide"
      >
        {{ promptHeaderText }}
      </h1>
      <!-- SPINNING ICON IF NO ACTION -->
      <font-awesome-icon
        v-else-if="!hasAction"
        icon="circle-notch"
        size="3x"
        class="animate-spin mx-auto mt-8 text-primary dark:text-primary-200"
      />
      <!-- CUSTOM COMPONENT IF SPECIFIED -->
      <component
        v-if="displayedComponent"
        :is="displayedComponent"
        @make-proceed-possible="makeProceedPossible"
        class="w-full h-full"
      />
      <!-- PROMPT TEXT OTHERWISE -->
      <p v-else class="my-8" :class="!hasAction && 'text-xl'">
        <span v-html="getPromptText" /></p>
      <!-- DO NOT SHOW AGAIN CHECKBOX -->
      <section v-if="showDoNotShowAgainCheckbox" @click="handleDoNotShow" class="flex w-full items-start justify-center pt-2 pb-12 cursor-pointer">
        <input
          type="checkbox"
          class="h-6 w-6 cursor-pointer rounded border-gray-300 dark:border-gray-800 text-primary dark:text-primary-200 focus:ring-primary mr-2"
          @click.stop
          @change="handleDoNotShow"
          @keydown.enter="handleDoNotShow"
          :checked="doNotShowBool"
        >
        <p class="text-gray-600 select-none">
          Do not show this again 
        </p>
      </section>
      <!-- ACCEPT AND REJECT BUTTONS -->
      <div v-if="hasAction && !hideDefaultButtons" class="flex w-full justify-center mb-8">
        <div class="w-full sm:w-4/5 flex flex-wrap sm:flex-nowrap gap-y-2">
          <VButton
            v-for="option of acceptanceOptions"
            :key="option"
            :label="option"
            :ref="option === acceptanceOptions[1] ? 'proceedBtn' : 'cancelBtn'"
            @click="submit(option)"
            :outlined="option === acceptanceOptions[0]"
            :disabled="option === acceptanceOptions[1] && !canProceed"
            uppercase
            class="p-2 mx-1 w-full"
            :class="acceptanceOptions.length === 2 && (option === acceptanceOptions[0] ? 'sm:mr-1 sm:w-1/2' : 'sm:ml-1 sm:w-1/2')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex"
import VButton from "@/components/Buttons/Button/VButton.vue"
import TakeOverPrompt from './TakeOverPrompt.vue'
import MoveEmployeePrompt from './MoveEmployeePrompt.vue'
import LogLaborPrompt from "./LogLaborPrompt.vue"
import WorkOrderSubmitPrompt from "./WorkOrderSubmitPrompt.vue"
import EditStatusPrompt from "./EditStatusPrompt.vue"
import ReceiptStatusCheckPrompt from "./ReceiptStatusCheckPrompt.vue"
import PostScheduleEditPrompt from "./PostScheduleEditPrompt.vue"
import ConfirmMultiEmployeeSiteEditPrompt from "../../../Configuration/components/Users/Prompts/ConfirmMultiEmployeeSiteEditPrompt.vue"
import EditSiteEmployeePrompt from "../../../Configuration/components/Users/Prompts/EditSiteEmployeePrompt.vue"
import SelectPermissionsForRegionPrompt from "../../../Configuration/components/Users/Prompts/SelectPermissionsForRegionPrompt.vue"
import RevokeInvitationPrompt from "../../../Configuration/components/Users/Prompts/RevokeInvitationPrompt.vue"
import EditOnHandPrompt from "../../../Site-Track/components/Inventory/Prompts/EditOnHandPrompt.vue"
import TaskSupportPrompt from "../../../Site-Track/components/Prompts/TaskSupportPrompt.vue"
import StoryPrompt from "./StoryPrompt.vue"

export default {
  name: "MessagePrompt",
  components: {
    VButton,
    TakeOverPrompt,
    MoveEmployeePrompt,
    LogLaborPrompt,
    WorkOrderSubmitPrompt,
    EditStatusPrompt,
    ReceiptStatusCheckPrompt,
    PostScheduleEditPrompt,
    ConfirmMultiEmployeeSiteEditPrompt,
    EditSiteEmployeePrompt,
    SelectPermissionsForRegionPrompt,
    RevokeInvitationPrompt,
    EditOnHandPrompt,
    TaskSupportPrompt,
    StoryPrompt,
  },
  data() {
    return {
      canProceed: true
    }
  },
  computed: {
    ...mapGetters([
      "modalOpen",
      "getPromptText",
      "getProceedAction",
      "getPromptSettings",
      "doNotShowBool",
      "getPromptDoNotShows"
    ]),
    hasAction() {
      return !!this.getProceedAction
    },
    displayedComponent() {
      if (!this.getPromptText.includes("COMPONENT: ")) return null
      else return this.getPromptText.substring(11)
    },
    promptHeaderText() {
      if (!this.getPromptSettings?.header) return "Are you sure?"
      else return this.getPromptSettings.header
    },
    hideDefaultButtons() {
      return this.getPromptSettings?.hideDefaultButtons
    },
    showCloseBtn() {
      return this.getPromptSettings?.showCloseBtn
    },
    showDoNotShowAgainCheckbox() {
      return this.getPromptSettings?.doNotShowAgain
        && !this.getPromptSettings.doNotShowAgain.clicked
        && !this.getPromptDoNotShows[this.getPromptSettings.doNotShowAgain.key]
    },
    promptClass() {
      let classes = "prompt-box w-11/12 bg-white rounded-lg dark:border dark:border-gray-700/60 shadow-xl flex flex-col px-4 "
      classes += this.hideDefaultButtons ? "md:w-auto " : "md:w-2/3 lg:w-2/5 "
      if (!this.showCloseBtn) classes += "sm:px-8 "
      if (this.getPromptSettings?.slideup) classes += "slideup-fast "
      return classes
    },
    acceptanceOptions() {
      if (!this.getPromptSettings?.promptBtns) return ["Cancel", "Proceed"]
      else return this.getPromptSettings?.promptBtns
    }
  },
  watch: {
    displayedComponent(val, oldVal) {
      if (val !== oldVal) {
        this.canProceed = true
      }
    }
  },
  mounted() {
    this.setPromptComment(null)
    document.querySelector("body").style.overflowY = "hidden"
    document.addEventListener("focusin", this.handleTab, true)
    if (this.$refs.proceedBtn) this.$refs.proceedBtn[0].$el.focus()
  },
  beforeUnmount() {
    if (!this.modalOpen) document.querySelector("body").style.overflowY = "auto"
    document.removeEventListener("focusin", this.handleTab, true)
  },
  methods: {
    ...mapMutations([
      "closePrompt",
      "proceedPrompt",
      "setPromptComment",
      "setPromptDoNotShowAgainBool"
    ]),
    submit(option) {
      if (option === "Cancel" || option === this.acceptanceOptions[0]) this.closePrompt()
      else this.proceedPrompt()
    },
    makeProceedPossible(bool) {
      this.canProceed = bool
    },
    handleTab(e) {
      if (!this.$refs.prompt) return
      const tabbedInsidePrompt = e.target === this.$refs.prompt
        || this.$refs.prompt.contains(e.target)
      if (!tabbedInsidePrompt) {
        let firstFocusable = this.$refs.prompt.querySelectorAll("button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])")
        if (firstFocusable.length) firstFocusable[0].focus()
      }
    },
    handleDoNotShow() {
      this.setPromptDoNotShowAgainBool(!this.doNotShowBool)
    }
  },
}
</script>

<style scoped>
p {
  white-space: pre-line;
}
/* ── User dropdown menu ─────────────────────────── */
[data-theme="dark"] .prompt-box {
  background: #080e18f7 !important;
  backdrop-filter: blur(4px);
  box-shadow: -7px -4px 33px -4px #65ceff07;
}
</style>